import axios from 'axios';
import qs from 'qs';

import ChallengeModel from './challenge-model.js';
import AreaModel from './area-model.js';

function logError(error) {
	console.error(error);
	throw error;
}

function buildChallengeDto(challenge) {
	const challengeDto = Object.assign({}, challenge);

	if (challengeDto.award || challengeDto.award === 0) {
		challengeDto.award = {
			amount: challenge.award,
			currencyCode: 'EUR',
		};
	} else {
		challengeDto.award = null;
	}

	return challengeDto;
}

function createChallengeDraft(challengeDraft, lang) {
	const challengeDto = buildChallengeDto(challengeDraft);

	return axios
		.post(
			`${process.env.REACT_APP_API_URL}/api/challenge/draft`,
			{
				...challengeDto,
			},
			{ params: { lang } },
		)
		.then(res => res.data);
}

function updateChallengeDraft(challengeDraft, lang) {
	const challengeDto = buildChallengeDto(challengeDraft);

	return axios
		.put(
			`${process.env.REACT_APP_API_URL}/api/challenge/draft/${challengeDto.id}`,
			{
				...challengeDto,
			},
			{ params: { lang } },
		)
		.then(res => res.data);
}

function createChallenge(challenge, lang) {
	const challengeDto = buildChallengeDto(challenge);

	return axios
		.post(
			`${process.env.REACT_APP_API_URL}/api/challenge`,
			{
				...challengeDto,
			},
			{ params: { lang } },
		)
		.then(res => res.data);
}

function updateChallenge(challenge, lang) {
	const challengeDto = buildChallengeDto(challenge);

	return axios
		.put(
			`${process.env.REACT_APP_API_URL}/api/challenge/${challengeDto.id}`,
			{
				...challengeDto,
			},
			{ params: { lang } },
		)
		.then(res => res.data);
}

class ChallengesService {
	getHelpTooltips(language) {
		return axios
			.get(
				`${process.env.REACT_APP_CMS_URL}/api/singletons/get/challenge_submission_form?token=&lang=${language}`,
			)
			.then(res => res.data)
			.catch(logError);
	}

	getChallengeDetails({ id, lang = 'en_GB' }) {
		return axios
			.get(`${process.env.REACT_APP_API_URL}/api/challenge/${id}`, {
				params: { lang, solutionStats: true },
			})
			.then(res => new ChallengeModel(res.data));
	}

	getChallengeLatestRevision({ challengeId, lang = 'en_GB' }) {
		return axios
			.get(
				`${process.env.REACT_APP_API_URL}/api/challenge/${challengeId}/latestrevision`,
				{ params: { lang } },
			)
			.then(res => new ChallengeModel(res.data));
	}

	submitChallenge(challenge, isDraft, lang = 'en_GB') {
		let retVal;

		if (isDraft) {
			retVal = challenge.id
				? updateChallengeDraft(challenge, lang)
				: createChallengeDraft(challenge, lang);
		} else {
			retVal = challenge.id
				? updateChallenge(challenge, lang)
				: createChallenge(challenge, lang);
		}

		return retVal;
	}

	deleteChallengeDraft(challengeId) {
		return axios
			.delete(
				`${process.env.REACT_APP_API_URL}/api/challenge/draft/${challengeId}`,
			)
			.then(res => res);
	}

	getChallenges({
		page = 0,
		size = 7,
		search = '',
		sortby,
		category,
		expiryDate,
		submittedDate,
		prize,
		company,
		lang = 'en_GB',
		status = 'OPEN,EVALUATION,CLOSED',
	} = {}) {
		const award = prize ? `award>${prize.min},award<${prize.max}` : null;

		const organisation = company ? `seeker.organisation.name:${company}` : null;

		const filterObj = qs
			.stringify(
				{
					deadline: expiryDate,
					submittedDate,
				},

				// replace '='to ':' because param 'filter' is waiting an object where its properties have values assigned ':'
				{ delimiter: ',', skipNulls: true },
			)
			.replace(/=/g, ':');

		let filter;

		filter = award
			? filterObj
				? filterObj.concat(',', award)
				: award
			: filterObj;

		filter = organisation
			? filter
				? filter.concat(',', organisation)
				: organisation
			: filter;

		var searchParams = new URLSearchParams({
			search,
			page,
			size,
			category,
			filter,
			lang,
			solutionStats: true,
			status,
		});

		if (!sortby) {
			searchParams.append('sort', 'status,desc');
			searchParams.append('sort', 'deadline,asc');
		} else {
			searchParams.append('sort', sortby);
		}

		return axios
			.get(`${process.env.REACT_APP_API_URL}/api/challenge/all`, {
				params: searchParams,
			})
			.then(res => res.data);
	}

	getAutocompleteChallenges({ search = '', size = 10 } = {}) {
		return axios
			.get(`${process.env.REACT_APP_API_URL}/api/challenge/searchauto`, {
				params: {
					text: search,
					size,
					status: 'OPEN,CLOSED,EVALUATION',
				},
			})
			.then(res => res.data);
	}

	getAllOrganisations({ page = 0, size = 10 } = {}) {
		return axios
			.get(`${process.env.REACT_APP_API_URL}/api/organisation`, {
				params: { page, size },
			})
			.then(res => res.data);
	}

	searchOrganisation({ page = 0, size = 10, searchTerm = '' } = {}) {
		return axios
			.get(`${process.env.REACT_APP_API_URL}/api/organisation/searchauto`, {
				params: { text: searchTerm, page, size },
			})
			.then(res => res.data);
	}

	getAllAreas({ page = 0, size = 10, lang = 'en_GB' } = {}) {
		return axios
			.get(`${process.env.REACT_APP_API_URL}/api/challenge/area`, {
				params: { lang, page, size },
			})
			.then(res => ({
				first: res.data.first,
				last: res.data.last,
				areas: res.data.content.map(
					area => new AreaModel({ id: area.id, name: area.name }),
				),
			}));
	}

	licenseAvailable() {
		return axios
			.get(`${process.env.REACT_APP_API_URL}/api/license/available`)
			.then(res => res.data);
	}

	searchAreas({ page = 0, size = 10, searchTerm = '', lang = 'en_GB' } = {}) {
		return axios
			.get(`${process.env.REACT_APP_API_URL}/api/challenge/area/searchauto`, {
				params: {
					text: searchTerm,
					page,
					size,
					lang,
				},
			})
			.then(res => res.data);
	}

	getChallengeTypes({ lang = 'en_GB' } = {}) {
		return axios
			.get(`${process.env.REACT_APP_API_URL}/api/challenge/type`, {
				params: { lang },
			})
			.then(res => res.data.content);
	}

	getChallengePrizeInterval() {
		return axios
			.get(`${process.env.REACT_APP_API_URL}/api/challenge/prize-interval`)
			.then(res => res.data);
	}

	getSubmissionAssistantPopularSubject(challengeAreasIds) {
		return axios
			.get(
				`${process.env.REACT_APP_API_URL}/api/challenge/assistantChallenge?challengeAreasIds=${challengeAreasIds}`,
			)
			.then(res => res.data);
	}

	getSubmissionAssistantSupply(challengeAreasIds) {
		return axios
			.get(
				`${process.env.REACT_APP_API_URL}/api/challenge/assistantSolver?challengeAreasIds=${challengeAreasIds}`,
			)
			.then(res => res.data);
	}

	getSubmissionAssistantDemand(challengeAreasIds) {
		return axios
			.get(
				`${process.env.REACT_APP_API_URL}/api/solution/assistantSolverDemanding?challengeAreasIds=${challengeAreasIds}`,
			)
			.then(res => res.data);
	}
}

export default new ChallengesService();
